import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Title,
  Text,
  Alert,
  Divider,
  Flex,
} from "@mantine/core";
import { IconAlertCircle, IconUser, IconShieldLock } from "@tabler/icons-react";

const LoginForm = () => {
  const [loginType, setLoginType] = useState(null);
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result = await login(formData.username, formData.password, loginType);

    if (result.success) {
      if (loginType === "admin") navigate("/admin-dashboard");
      else navigate("/patient-form");
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(""); // clear error once user edits
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #7acdf3ff, #f3fafaff)", // light blue → light purple
      }}
    >
      <Flex
        justify="center"
        align="center"
        style={{ minHeight: "100vh", padding: 20 }}
      >
        <Paper
          shadow="xl"
          radius="lg"
          p="xl"
          withBorder
          style={{
            width: "90%",
            maxWidth: 600,
            minHeight: 400,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* main content (fills space) */}
          <div style={{ flex: 1 }}>
            {/* Header */}
            <Stack align="center" spacing="xs" mb="lg">
              <Title
                order={2}
                weight={700}
                style={{
                  background: "linear-gradient(90deg, #4F46E5, #A78BFA)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Welcome to Althing AIRs Sign Up Portal
              </Title>
              <Text size="md" c="dimmed" ta="center"></Text>
            </Stack>

            {/* Login buttons or form */}
            {!loginType ? (
              <Flex gap="md" justify="center" wrap="wrap">
                <Button
                  size="lg"
                  style={{ flex: 1, minWidth: 200 }}
                  leftSection={<IconUser size={20} />}
                  variant="gradient"
                  gradient={{ from: "blue", to: "violet" }}
                  onClick={() => setLoginType("user")}
                >
                  User Login
                </Button>
                <Button
                  size="lg"
                  style={{ flex: 1, minWidth: 200 }}
                  leftSection={<IconShieldLock size={20} />}
                  variant="default"
                  onClick={() => setLoginType("admin")}
                >
                  Admin Login
                </Button>
              </Flex>
            ) : (
              <form onSubmit={handleSubmit}>
                <Stack spacing="md">
                  <Title order={4} ta="center">
                    {loginType === "admin" ? "Admin" : "User"} Login
                  </Title>

                  {error && (
                    <Alert
                      icon={<IconAlertCircle size={18} />}
                      title="Login Failed"
                      color="red"
                      variant="light"
                    >
                      {error}
                    </Alert>
                  )}

                  <TextInput
                    label="Username"
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                    required
                  />

                  <PasswordInput
                    label="Password"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    required
                  />

                  <Flex gap="md" justify="center" wrap="wrap">
                    <Button
                      type="submit"
                      size="lg"
                      style={{ flex: 1, minWidth: 180 }}
                      loading={loading}
                      variant="gradient"
                      gradient={{ from: "blue", to: "violet" }}
                    >
                      {loading ? "Logging in..." : "Login"}
                    </Button>
                    <Button
                      size="lg"
                      style={{ flex: 1, minWidth: 180 }}
                      variant="subtle"
                      onClick={() => setLoginType(null)}
                    >
                      Back
                    </Button>
                  </Flex>
                </Stack>
              </form>
            )}
          </div>

          {/* Footer Logo */}
          <Divider my="lg" labelPosition="center" />
          <Flex
            justify="center"
            align="center"
            wrap="wrap" // allows items to wrap on small screens
            style={{ minHeight: 100, gap: 10, padding: "0 1rem" }}
          >
            <img
              src="/assets/althing_clinic_banner.png"
              alt="Portal Banner"
              style={{
                height: 80,
                maxWidth: "100%",
                width: "auto",
                objectFit: "contain",
              }}
            />
            <span
              style={{
                fontWeight: "bold",
                fontSize: 34,
                margin: "0 10px",
                whiteSpace: "nowrap",
              }}
            >
              ×
            </span>
            <img
              src="/althing_airs_logo.png"
              alt="Second Logo"
              style={{
                height: 80, // made it same as the first image for consistency
                maxWidth: "100%",
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Flex>
        </Paper>
      </Flex>
    </div>
  );
};

export default LoginForm;
